"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useFormContext, useWatch } from "react-hook-form";
import {
	Autocomplete,
	Button,
	Chip,
	HStack,
	IconButton,
	SimpleDialog,
	Stack,
	Text,
} from "@leabankab/component-library";
import { FormField } from "@leabankab/component-library/react-hook-form";
import {
	InputField,
	NumberField,
	RadioCardField,
	SelectField,
	focusFirstErrorAfterRender,
} from "@/components/form";
import { Collapse } from "@/components/ui";
import { useCountryConfig } from "@/config/country-context";
import { debtTypeFromCreditCheckDescription } from "@/features/confirm-debt/utils/debt-types";
import {
	useConstantSelectOptions,
	useSortedConstantSelectOptions,
} from "@/features/customer-information/hooks/use-constant-options";
import {
	countIncompleteDebtFields,
	needsCompletion,
} from "@/features/refinance/hooks/use-refinance-form";
import { useSaveDebt } from "@/features/refinance/hooks/use-save-debt";
import { RefinanceDebt } from "@/features/refinance/types";
import { withoutLeaBank } from "@/features/refinance/utils/lea-bank-debts";
import { useCurrency } from "@/hooks";
import { useRevealOnFailedSubmit } from "@/hooks/form/use-reveal-on-failed-submit";
import { GiroType } from "@/types";

interface DebtCardProps {
	index: number;
	removable?: boolean;
	defaultExpanded?: boolean;
	autoFocus?: boolean;
	seed?: RefinanceDebt;
	/** Bumped when the list validates its unfinished debts. */
	revealAttempt?: number;
	customerAccountNumber?: string | null;
	onRemove?: () => void;
}

export const DebtCard = ({
	index,
	removable = false,
	defaultExpanded = false,
	autoFocus = false,
	seed,
	revealAttempt,
	customerAccountNumber,
	onRemove,
}: DebtCardProps) => {
	const t = useTranslations("pages.RefinancePage");
	const tCommon = useTranslations("common");
	const tDebtTypes = useTranslations("pages.ConfirmDebtPage.debtTypes");
	const { control, trigger, setValue, getValues, clearErrors } =
		useFormContext();
	const { format, suffix } = useCurrency();

	const typeOptions = useConstantSelectOptions("loan-types");
	const bankOptions = useSortedConstantSelectOptions("banks");
	const creditGiverOptions = useMemo(
		() => withoutLeaBank(bankOptions),
		[bankOptions]
	);

	const debt = useWatch({ control, name: `debts.${index}` }) as
		RefinanceDebt | undefined;

	const namePrefix = `debts.${index}`;
	const contentId = `debt-card-${index}-content`;
	const { country, rules } = useCountryConfig();
	const isGiroOcr = rules.redemptionPayment === "giro-ocr";
	const fieldsRemaining = countIncompleteDebtFields(
		debt ?? {},
		country,
		customerAccountNumber
	);
	const isComplete = fieldsRemaining === 0;
	const saved = (debt?.saved ?? false) && isComplete;
	const [expanded, setExpanded] = useState(defaultExpanded);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const cardRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLButtonElement>(null);
	const deleteRef = useRef<HTMLButtonElement>(null);
	// Indices shift on removal, so a mounted card must not focus itself again.
	const focusOnMount = useRef(autoFocus);
	const openedForCompletion = useRef(false);

	useEffect(() => {
		if (focusOnMount.current) {
			headerRef.current?.focus();
		}
	}, []);

	useEffect(() => {
		if (
			openedForCompletion.current ||
			!needsCompletion(debt ?? {}, country, customerAccountNumber)
		)
			return;

		openedForCompletion.current = true;
		setExpanded(true);
	}, [debt, country, customerAccountNumber]);

	useRevealOnFailedSubmit(namePrefix, () => setExpanded(true), revealAttempt);

	// The list saves a finished debt when another is added, and it closes too.
	const savedFlag = debt?.saved ?? false;
	const wasSaved = useRef(savedFlag);
	useEffect(() => {
		if (savedFlag && !wasSaved.current) setExpanded(false);
		wasSaved.current = savedFlag;
	}, [savedFlag]);

	const saveDebt = useSaveDebt(customerAccountNumber);

	const internalRedemption = debt?.internalRedemption ?? false;
	const giroType: GiroType = debt?.giroType ?? "bankgiro";

	// `Collapse` unmounts on exit, taking the focused Save or Cancel button with it.
	const collapse = () => {
		headerRef.current?.focus();
		setExpanded(false);
	};

	const handleSave = () => {
		if (!saveDebt(index)) {
			focusFirstErrorAfterRender(cardRef);
			return;
		}
		collapse();
	};

	const handleReset = () => {
		setValue(`${namePrefix}.description`, seed?.description ?? "", {
			shouldDirty: true,
		});
		setValue(`${namePrefix}.bank`, seed?.bank ?? "", { shouldDirty: true });
		setValue(`${namePrefix}.amount`, seed?.amount, { shouldDirty: true });
		setValue(`${namePrefix}.giroType`, "bankgiro", { shouldDirty: true });
		setValue(`${namePrefix}.iban`, "", { shouldDirty: true });
		setValue(`${namePrefix}.loanNumber`, "", { shouldDirty: true });
		setValue(`${namePrefix}.saved`, seed?.internalRedemption ?? false, {
			shouldDirty: true,
		});
		clearErrors(namePrefix);
		collapse();
	};

	// The dialog's focus restore is off, since confirming unmounts its target.
	const dismissDialog = () => {
		setConfirmOpen(false);
		deleteRef.current?.focus();
	};

	const typeLabel = typeOptions.find(
		(option) => option.value === debt?.description
	)?.label;
	const bankLabel = bankOptions.find(
		(option) => option.value === debt?.bank
	)?.label;

	const sourceType = debtTypeFromCreditCheckDescription(debt?.sourceLabel);
	const headerTitle =
		bankLabel ??
		(sourceType ? tDebtTypes(sourceType) : debt?.sourceLabel) ??
		t("debtCard.title");

	const originalAmount = debt?.originalAmount;
	const amountEdited =
		originalAmount != null && debt?.amount !== originalAmount;

	const handleAmountBlur = () => {
		if (originalAmount != null && getValues(`${namePrefix}.amount`) == null) {
			setTimeout(() =>
				setValue(`${namePrefix}.amount`, originalAmount, {
					shouldDirty: true,
					shouldValidate: true,
				})
			);
			return;
		}

		void trigger(`${namePrefix}.amount`);
	};

	const giroOptions: { value: string; label: string }[] = [
		{ value: "bankgiro", label: t("debtCard.giroOptions.bankgiro") },
		{ value: "plusgiro", label: t("debtCard.giroOptions.plusgiro") },
	];

	const giroNumberLabel =
		isGiroOcr && giroType === "plusgiro"
			? t("debtCard.fields.plusgiroNumber")
			: t("debtCard.fields.bankgiroNumber");

	// The credit check may report a type we cannot match to an option, in which
	// case the customer still has to pick one.
	const typeFromCreditCheck = !!seed?.description;

	return (
		<>
			<div
				ref={cardRef}
				className={`relative rounded-xl border ${
					saved
						? "border-primary-base bg-purple-50"
						: "border-border-base bg-transparent"
				}`}
			>
				<div className="absolute top-[-12px] right-[10px]">
					<Chip
						label={
							saved
								? t("debtCard.badge.complete")
								: t("debtCard.fieldsRemaining", { count: fieldsRemaining })
						}
						size="sm"
						color={saved ? "primary" : "neutral"}
						variant={saved ? "filled" : "outlined"}
						className={saved ? undefined : "bg-white"}
					/>
				</div>
				<HStack align="center" gap={1} className="px-2 py-1.5">
					<button
						ref={headerRef}
						type="button"
						aria-expanded={expanded}
						aria-controls={contentId}
						onClick={() => setExpanded((value) => !value)}
						className="flex w-full grow cursor-pointer items-center justify-between gap-1 border-0 bg-transparent p-0 text-left"
					>
						<Stack gap={0.5} className="flex-1">
							<HStack align="center" gap={1} className="w-full">
								<Text fontWeight="medium" color="text.base">
									{headerTitle}
								</Text>
								{typeLabel && (
									<Chip
										label={typeLabel}
										size="sm"
										variant="filled"
										color="neutral"
									/>
								)}
							</HStack>
							<Text color="text.base">{format(Number(debt?.amount ?? 0))}</Text>
						</Stack>

						{expanded ? (
							<ChevronUp size={20} aria-hidden="true" />
						) : (
							<ChevronDown size={20} aria-hidden="true" />
						)}
					</button>

					{removable && (
						<IconButton
							ref={deleteRef}
							aria-label={t("debtCard.deleteModal.confirm")}
							size="sm"
							variant="ghost"
							onClick={() => setConfirmOpen(true)}
						>
							<Trash2 size={18} aria-hidden="true" />
						</IconButton>
					)}
				</HStack>

				<Collapse open={expanded} id={contentId}>
					<Stack gap={2} className="px-2 pb-2">
						<SelectField
							name={`${namePrefix}.description`}
							label={t("debtCard.fields.type")}
							options={typeOptions}
							disabled={typeFromCreditCheck}
						/>
						<FormField
							name={`${namePrefix}.bank`}
							render={({ field }) => (
								<Autocomplete
									label={t("debtCard.fields.creditGiver")}
									options={creditGiverOptions}
									placeholder={t("debtCard.fields.creditGiverPlaceholder")}
									{...field}
									value={field.value ?? ""}
								/>
							)}
						/>
						<div onBlur={handleAmountBlur}>
							<NumberField
								name={`${namePrefix}.amount`}
								label={t("debtCard.fields.amount")}
								unit={suffix}
								showSteppers={false}
								helperText={
									amountEdited
										? t("debtCard.originalAmount", {
												amount: format(originalAmount),
											})
										: undefined
								}
							/>
						</div>
						{!internalRedemption && (
							<>
								{isGiroOcr && (
									<RadioCardField
										name={`${namePrefix}.giroType`}
										label={t("debtCard.fields.giroType")}
										options={giroOptions}
									/>
								)}

								<InputField
									name={`${namePrefix}.iban`}
									label={giroNumberLabel}
								/>
								<InputField
									name={`${namePrefix}.loanNumber`}
									label={t("debtCard.fields.ocr")}
								/>
							</>
						)}
						<HStack gap={1} justify="end">
							<Button variant="outline" onClick={handleReset}>
								{tCommon("clear")}
							</Button>
							<Button onClick={handleSave}>{tCommon("save")}</Button>
						</HStack>
					</Stack>
				</Collapse>
			</div>

			<SimpleDialog
				open={confirmOpen}
				onOpenChange={(open) => {
					if (!open) dismissDialog();
				}}
				title={t("debtCard.deleteModal.header")}
				description={t("debtCard.deleteModal.body")}
				closeLabel={t("debtCard.deleteModal.cancel")}
			>
				<HStack gap={1} justify="end">
					<Button variant="outline" onClick={dismissDialog} size="sm">
						{t("debtCard.deleteModal.cancel")}
					</Button>
					<Button
						onClick={() => {
							setConfirmOpen(false);
							onRemove?.();
						}}
						size="sm"
					>
						{t("debtCard.deleteModal.confirm")}
					</Button>
				</HStack>
			</SimpleDialog>
		</>
	);
};
